// API 응답 데이터 변환 유틸리티

import _ from "lodash";

// 기본 옵션 타입
export interface SelectOption {
  label: string;
  value: string;
  isSelected?: boolean;
}

// 범용 변환 함수 - 코드와 이름 필드를 자동으로 매핑
export function transformToOptions<T extends Record<string, unknown>>(
  items: T[],
  codeField: keyof T,
  nameField: keyof T
): SelectOption[] {
  if (!Array.isArray(items)) {
    console.warn("transformToOptions: items is not an array", items);
    return [];
  }

  return items.map((item) => ({
    value: String(item[codeField] || ""),
    label: String(item[nameField] || ""),
  }));
}

// 자동 필드 감지 변환 함수
export function autoTransformToOptions(
  items: Record<string, unknown>[]
): SelectOption[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const firstItem = items[0];
  const keys = Object.keys(firstItem);

  // 코드 필드 찾기 (cd로 시작하는 필드)
  const codeField = keys.find(
    (key) =>
      key.startsWith("cd") ||
      key.toLowerCase().includes("code") ||
      key.toLowerCase().includes("id")
  );

  // 이름 필드 찾기 (Name으로 끝나는 필드)
  const nameField = keys.find(
    (key) =>
      key.toLowerCase().includes("name") ||
      key.toLowerCase().includes("label") ||
      key.toLowerCase().includes("title")
  );

  if (!codeField || !nameField) {
    console.warn("autoTransformToOptions: Could not find code or name field", {
      keys,
      firstItem,
    });
    return [];
  }

  return transformToOptions(items, codeField, nameField);
}

// 키-값 쌍 객체를 SelectOption으로 변환
export function transformKeyValueToOptions(
  item: Record<string, string>
): SelectOption[] {
  return Object.entries(item).map(([key, value]) => ({
    value: key,
    label: value,
  }));
}

// 키-값 쌍 객체 배열을 SelectOption으로 변환
export function transformKeyValueArrayToOptions(
  items: Record<string, string>[]
): SelectOption[] {
  if (!Array.isArray(items)) {
    console.warn(
      "transformKeyValueArrayToOptions: items is not an array",
      items
    );
    return [];
  }

  const result: SelectOption[] = [];

  items.forEach((item) => {
    const options = transformKeyValueToOptions(item);
    result.push(...options);
  });

  return result;
}

// 중첩된 데이터 구조 처리 함수
export function transformNestedDataToOptions(data: {
  [key: string]: Record<string, string>[] | Record<string, string> | unknown;
}): Record<string, SelectOption[]> {
  const result: Record<string, SelectOption[]> = {};

  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // 배열인 경우: 키-값 쌍 객체들의 배열
      result[key] = transformKeyValueArrayToOptions(
        value as Record<string, string>[]
      );
    } else if (typeof value === "object" && value !== null) {
      // 단일 객체인 경우: 키-값 쌍 객체
      result[key] = transformKeyValueToOptions(value as Record<string, string>);
    }
  });

  return result;
}

// value-label 쌍이 번갈아 나오는 데이터를 SelectOption으로 변환
export function transformAlternatingPairsToOptions(
  items: Array<{ value: string; label: string }>
): SelectOption[] {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const result: SelectOption[] = [];

  // 2개씩 묶어서 처리 (코드, 이름 순서)
  for (let i = 0; i < items.length; i += 2) {
    const codeItem = items[i];
    const nameItem = items[i + 1];

    if (codeItem && nameItem) {
      result.push({
        value: codeItem.label, // 코드 값
        label: nameItem.label, // 이름 값
      });
    }
  }

  return result;
}

// 중첩된 번갈아 나오는 데이터 구조 처리
export function transformNestedAlternatingDataToOptions(data: {
  [key: string]: Array<{ value: string; label: string }> | unknown;
}): Record<string, SelectOption[]> {
  const result: Record<string, SelectOption[]> = {};
  console.log(data);
  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      result[key] = transformAlternatingPairsToOptions(value);
    }
  });

  return result;
}

// 사용 예시:
/*
const apiData = {
  "grades": [
    {
      "cdGradeCode": "PET_GRADE_001",
      "gradeName": "소형견"
    }
  ],
  "providedServices": [
    {
      "cdProvideService": "PROVIDED_SERVICE_001", 
      "gradeName": "유치원"
    }
  ]
};

// 사용법:
const transformedData = transformApiResponse(apiData);
// 결과: 
// {
//   grades: [{ value: "PET_GRADE_001", label: "소형견" }],
//   providedServices: [{ value: "PROVIDED_SERVICE_001", label: "유치원" }]
// }

// 또는 개별 변환:
const gradeOptions = transformGradesToOptions(apiData.grades);
const serviceOptions = transformServicesToOptions(apiData.providedServices);

// 자동 감지:
const autoOptions = autoTransformToOptions(apiData.grades);
*/

/**
 * 전화번호를 010-1234-5678 형식으로 포맷팅
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  // 숫자만 추출
  const numbers = phone.replace(/\D/g, "");

  // 11자리 휴대폰 번호 포맷팅
  if (numbers.length === 11) {
    return numbers.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  }

  // 10자리 전화번호 포맷팅
  if (numbers.length === 10) {
    return numbers.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  }

  return phone; // 원본 그대로 반환
};

/**
 * 사업자번호를 123-45-67890 형식으로 포맷팅
 */
export const formatBusinessNumber = (businessNumber: string): string => {
  if (!businessNumber) return "";

  // 숫자만 추출
  const numbers = businessNumber.replace(/\D/g, "");

  // 10자리 사업자번호 포맷팅
  if (numbers.length === 10) {
    return numbers.replace(/(\d{3})(\d{2})(\d{5})/, "$1-$2-$3");
  }

  return businessNumber; // 원본 그대로 반환
};

/**
 * 이메일 형식 검증
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * 한글 이름 형식 검증
 */
export const isValidKoreanName = (name: string): boolean => {
  const koreanNameRegex = /^[가-힣]{2,10}$/;
  return koreanNameRegex.test(name);
};

/**
 * 휴대폰 번호 입력 시 자동 하이픈 삽입
 */
export const formatPhoneInput = (value: string): string => {
  // 숫자만 추출
  const numbers = value.replace(/\D/g, "");

  // 길이에 따라 하이픈 삽입
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 7) {
    return numbers.replace(/(\d{3})(\d{0,4})/, "$1-$2");
  } else {
    return numbers.replace(/(\d{3})(\d{4})(\d{0,4})/, "$1-$2-$3");
  }
};

/**
 * 사업자번호 입력 시 자동 하이픈 삽입
 */
export const formatBusinessInput = (value: string): string => {
  // 숫자만 추출
  const numbers = value.replace(/\D/g, "");

  // 길이에 따라 하이픈 삽입
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 5) {
    return numbers.replace(/(\d{3})(\d{0,2})/, "$1-$2");
  } else {
    return numbers.replace(/(\d{3})(\d{2})(\d{0,5})/, "$1-$2-$3");
  }
};

/**
 * Input 컴포넌트의 tel 타입 포맷팅 로직 (실시간 입력용)
 */
export const formatTelInput = (value: string): string => {
  // 숫자가 아닌 모든 문자를 제거합니다.
  let formattedValue = value.replace(/\D/g, "");

  if (formattedValue.length > 3 && formattedValue.length <= 7) {
    formattedValue = formattedValue.replace(/(\d{3})(\d+)/, "$1-$2");
  } else if (formattedValue.length > 7) {
    formattedValue = formattedValue.replace(
      /(\d{3})(\d{3,4})(\d{1,4})/,
      "$1-$2-$3"
    );
  }
  return formattedValue.substring(0, 13);
};

/**
 * Input 컴포넌트의 business-number regexp 포맷팅 로직 (실시간 입력용)
 */
export const formatBusinessNumberInput = (value: string): string => {
  // 숫자가 아닌 모든 문자를 제거합니다.
  let formattedValue = value.replace(/\D/g, "");

  if (formattedValue.length > 3 && formattedValue.length <= 5) {
    formattedValue = formattedValue.replace(/(\d{3})(\d+)/, "$1-$2");
  } else if (formattedValue.length > 5) {
    formattedValue = formattedValue.replace(
      /(\d{3})(\d{2})(\d{1,5})/,
      "$1-$2-$3"
    );
  }
  return formattedValue.substring(0, 12); // 사업자번호 최대 길이 제한
};

/**
 * Input 컴포넌트의 date regexp 포맷팅 로직 (실시간 입력용)
 */
export const formatDateInput = (value: string): string => {
  // 숫자가 아닌 모든 문자를 제거합니다.
  let formattedValue = value.replace(/\D/g, "");

  if (formattedValue.length > 4 && formattedValue.length <= 6) {
    formattedValue = formattedValue.replace(/(\d{4})(\d+)/, "$1-$2");
  } else if (formattedValue.length > 6) {
    formattedValue = formattedValue.replace(
      /(\d{4})(\d{2})(\d{1,2})/,
      "$1-$2-$3"
    );
  }
  return formattedValue.substring(0, 10);
};

/**
 * Input 컴포넌트의 decimal 타입 포맷팅 로직 (실시간 입력용)
 */
export const formatDecimalInput = (value: string): string => {
  return value.replace(/[^\d.]/g, "");
};

export const removeNullInObject = (obj: Record<string, unknown>) => {
  return _.pickBy(obj, function (value) {
    return !_.isEmpty(value) || _.isNumber(value);
  });
};
